import { useCallback, useEffect, useState } from "react"
import type { AppShell } from "@/App"
import { errorMessage, importPostalBatch, listPostal, requoteAll, seedExamplePostal, uploadFile, type PostalList } from "@/lib/api"
import { AsyncButton, ErrorBox, FileButton, inputCls, Loading, Note, Page, Panel, Tag, Td, Th } from "@/components/kit"
import { cn } from "@/lib/utils"

const PRECISION: Record<string, string> = { cp7: "CP7", cp4: "só CP4", address: "pela morada", manual: "pino manual", none: "sem localização" }

export function PostalCodes({ shell }: { shell: AppShell }) {
  const [data, setData] = useState<PostalList | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [cp4, setCp4] = useState("")
  const [progress, setProgress] = useState<string | null>(null)
  const canEdit = shell.role >= 5

  const load = useCallback(async (filter?: string) => {
    try {
      setData(await listPostal(shell, filter))
    } catch (err) {
      setError(errorMessage(err))
    }
  }, [shell])
  useEffect(() => void load(), [load])

  async function relocate() {
    try {
      const total = await requoteAll(shell, true, (d, t) => setProgress(`A relocalizar encomendas ${d}/${t}…`))
      shell.toast("success", "Encomendas relocalizadas", `${total} em aberto`)
    } catch (err) {
      shell.toast("error", "A relocalização parou a meio", `${errorMessage(err)} — carrega em «Relocalizar encomendas» para continuar.`)
    } finally {
      setProgress(null)
      await load(cp4 || undefined)
    }
  }

  if (error) return <Page><ErrorBox error={error} /></Page>
  if (!data) return <Loading />

  return (
    <Page>
      <Panel
        title="Tabela de códigos postais"
        subtitle="Localização de cada CP (CP7 quando o ficheiro o traz, senão o centróide do CP4). É o primeiro passo da localização: CP7 → CP4 → geocode da morada → pino manual."
        actions={canEdit && (
          <>
            {data.items.length === 0 && (
              <AsyncButton size="sm" variant="outline" onRun={async () => {
                const r = await seedExamplePostal(shell)
                shell.toast("success", "Exemplo carregado", `${r.inserted} CP4`)
                await relocate()
              }}>Usar os 42 CP4 de exemplo</AsyncButton>
            )}
            <AsyncButton size="sm" variant="outline" disabled={!!progress} onRun={relocate} title="Volta a localizar as encomendas em aberto pela tabela (CP7 → CP4 → morada)">
              Relocalizar encomendas
            </AsyncButton>
            <FileButton size="sm" accept=".xlsx,.xls,.csv" onFiles={async (files) => {
              // Vários ficheiros de uma vez (a tabela partida em ficheiros de ~1 MB):
              // um de cada vez, por lotes; um que falhe não trava os outros.
              let inserted = 0
              let updated = 0
              let rejected = 0
              const failed: string[] = []
              const sorted = [...files].sort((a, b) => a.name.localeCompare(b.name, "pt", { numeric: true }))
              for (const [i, file] of sorted.entries()) {
                const tag = sorted.length > 1 ? `Ficheiro ${i + 1}/${sorted.length} (${file.name}): ` : ""
                try {
                  setProgress(`${tag}a enviar…`)
                  const key = await uploadFile(shell, file)
                  let offset: number | null = 0
                  while (offset !== null) {
                    const r = await importPostalBatch(shell, key, offset)
                    inserted += r.inserted
                    updated += r.updated
                    if (offset === 0) rejected += r.rejected
                    setProgress(`${tag}a importar ${r.done}/${r.total}…`)
                    offset = r.next_offset
                  }
                } catch (e) {
                  failed.push(`${file.name}: ${errorMessage(e)}`)
                }
              }
              setProgress(null)
              if (failed.length) shell.toast("error", `${failed.length} ficheiro(s) não importado(s)`, failed.join(" · ").slice(0, 400))
              shell.toast("success", "Códigos postais importados", `${inserted} novos, ${updated} atualizados${rejected ? `, ${rejected} linhas recusadas` : ""}`)
              await relocate()
            }}>Importar ficheiros de CP</FileButton>
          </>
        )}
      >
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-muted-foreground">
          <span>Encomendas em aberto:</span>
          {Object.entries(PRECISION).map(([k, label]) => (
            <span key={k} className={cn(k === "none" && data.by_precision[k] && "text-red-700")}>
              <b className="font-mono text-foreground">{data.by_precision[k] ?? 0}</b> {label}
            </span>
          ))}
        </div>
        {progress && <div className="mt-2 text-sm text-blue-700">{progress}</div>}
        <Note>Colunas do ficheiro: CP7 (ou CP4 + extensão), latitude, longitude, localidade, distrito. O dos CTT traz ~200 mil linhas e entra por lotes. Depois de importar, as encomendas relocalizam-se.</Note>
      </Panel>

      {data.unmatched.length > 0 && (
        <Panel title="CP de encomenda sem correspondência" subtitle="Encomendas em aberto localizadas só pelo CP4, pela morada ou nada. Corrige o CP na encomenda ou acrescenta-o à tabela.">
          <div className="flex flex-wrap gap-2">
            {data.unmatched.map((u) => (
              <span key={u.postal_code} className="inline-flex items-center gap-2 rounded-md border px-2 py-1 text-xs">
                <span className="font-mono">{u.postal_code}</span>
                {u.locality && <span className="text-muted-foreground">{u.locality}</span>}
                <Tag tone={u.precision === "none" ? "bad" : "warn"}>{PRECISION[u.precision]}</Tag>
                <span className="font-mono text-muted-foreground">{u.orders} enc.</span>
              </span>
            ))}
          </div>
        </Panel>
      )}

      <Panel
        title="Códigos postais"
        actions={
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); void load(cp4 || undefined) }}>
            <input className={cn(inputCls, "w-28")} placeholder="CP4" value={cp4} onChange={(e) => setCp4(e.target.value)} />
          </form>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead><tr><Th>CP4</Th><Th>CP7</Th><Th>Localidade</Th><Th>Distrito</Th><Th>Lat / Lon</Th></tr></thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.id}>
                  <Td className="font-mono text-xs">{p.cp4}</Td>
                  <Td className="font-mono text-xs">{p.cp7 ?? <span className="text-muted-foreground">centróide</span>}</Td>
                  <Td>{p.locality ?? "—"}</Td>
                  <Td>{p.district ?? "—"}</Td>
                  <Td className="font-mono text-xs">{Number(p.lat).toFixed(4)}, {Number(p.lng).toFixed(4)}</Td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><Td colSpan={5} className="py-8 text-center text-muted-foreground">Tabela vazia: importa o ficheiro do cliente (§9.4) ou usa o exemplo.</Td></tr>}
            </tbody>
          </table>
        </div>
        {data.next_offset !== null && <Note>Mostra os primeiros 500; filtra por CP4 para ver o resto.</Note>}
      </Panel>
    </Page>
  )
}
